import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { authenticatedNavigationItems } from '../config/navigation'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { reportsAPI, appealsAPI } from '../config/api'
import 'primeicons/primeicons.css'

export default function HeaderAuthenticated() {
  const location = useLocation()
  const pathname = location.pathname
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [reportsCount, setReportsCount] = useState(0)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const { theme, toggleTheme } = useTheme()
  const { user, logout, isAuthenticated } = useAuth()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [pathname])

  // Fetch số lượng báo cáo chưa xử lý
  useEffect(() => {
    if (isAuthenticated && user) {
      const fetchReports = async () => {
        try {
          const [reports, appeals] = await Promise.all([
            reportsAPI.getMyReportsReceived().catch(() => []),
            appealsAPI.getAppeals().catch(() => [])
          ])
          
          // Đảm bảo là array
          const reportsArray = Array.isArray(reports) ? reports : []
          const appealsArray = Array.isArray(appeals) ? appeals : []
          
          // Đếm số báo cáo chưa có kháng cáo và có thể kháng cáo (status = resolved)
          // hoặc đang chờ xử lý (pending, investigating)
          const unresolvedCount = reportsArray.filter((r: any) => {
            // Kiểm tra xem đã có kháng cáo chưa
            const hasAppeal = appealsArray.some((a: any) => 
              a.report === r.id || a.report?.id === r.id
            )
            
            // Đếm các báo cáo:
            // 1. Chưa có kháng cáo
            // 2. Status là resolved (có thể kháng cáo) hoặc pending/investigating (đang chờ xử lý)
            return !hasAppeal && (
              r.status === 'resolved' || 
              r.status === 'pending' || 
              r.status === 'investigating'
            )
          }).length
          
          setReportsCount(unresolvedCount)
        } catch (error) {
          // Ignore errors
          setReportsCount(0)
        }
      }
      fetchReports()
      // Refresh mỗi 30 giây
      const interval = setInterval(fetchReports, 30000)
      return () => clearInterval(interval)
    }
  }, [isAuthenticated, user])

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${theme === 'dark'
        ? isScrolled
          ? 'bg-black/95 backdrop-blur-md border-b-2 border-gold-500 shadow-lg shadow-gold-500/20'
          : 'bg-black border-b-2 border-gold-500'
        : isScrolled
          ? 'bg-gray-200/95 backdrop-blur-md border-b-2 border-gold-500 shadow-lg shadow-gold-500/20'
          : 'bg-gray-200 border-b-2 border-gold-500'
        }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo Section */}
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 md:gap-4 hover:opacity-80 transition-all duration-300 group flex-shrink-0"
          >
            <div className="relative flex-shrink-0">
              <img
                src="/images/logo.png"
                alt="SAIGON FC Logo"
                width={isScrolled ? 45 : 50}
                height={isScrolled ? 45 : 50}
                className="object-contain transition-all duration-300"
              />
            </div>
            <div className="flex flex-col leading-tight min-w-0 items-start">
              <span
                className={`font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'} transition-all duration-300 whitespace-nowrap ${isScrolled ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'
                  }`}
              >
                SAIGON FC
              </span>
              <span
                className={`font-semibold transition-all duration-300 whitespace-nowrap text-[10px] sm:text-xs ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
              >
                Training League
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-1 justify-end max-w-[60%]">
            <nav className="flex items-center gap-1 xl:gap-1.5">
              {authenticatedNavigationItems.map((item) => {
                const isActive = pathname === item.href
                const isReportsPage = item.href === '/reports-appeals'
                return (
                  <div key={item.href} className="flex-shrink-0">
                    <Link
                      to={item.href}
                      className={`relative flex items-center justify-center gap-1 px-2.5 xl:px-3 py-2 rounded-lg transition-all duration-300 group ${isActive
                        ? 'bg-gold-gradient text-black font-bold'
                        : theme === 'dark'
                          ? 'text-white hover:text-gold-400'
                          : 'text-gray-700 hover:text-gray-900'
                        }`}
                    >
                      {item.icon && (
                        <i
                          className={`${item.icon} text-xs xl:text-sm transition-transform duration-300 group-hover:scale-110 hidden xl:inline-block`}
                        ></i>
                      )}
                      <span className="font-semibold text-[10px] xl:text-xs whitespace-nowrap">
                        {item.name}
                      </span>
                      {isReportsPage && reportsCount > 0 && (
                        <span className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${
                          theme === 'dark' ? 'bg-red-500 text-white' : 'bg-red-500 text-white'
                        }`}>
                          {reportsCount}
                        </span>
                      )}
                    </Link>
                  </div>
                )
              })}
            </nav>

            {/* Theme Toggle & Auth Buttons Desktop */}
            <div className="flex items-center gap-1.5 xl:gap-2 ml-2 xl:ml-3 pl-2 xl:pl-3 border-l border-gold-500/30 flex-shrink-0">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className={`w-10 h-10 flex items-center justify-center transition-all duration-300 rounded-lg active:scale-95 ${theme === 'dark'
                  ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                  }`}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <i className="pi pi-sun text-lg"></i>
                ) : (
                  <i className="pi pi-moon text-lg"></i>
                )}
              </button>

              {/* User Avatar Dropdown */}
              {user && (
                <div className="flex items-center gap-3">
                  <div className="relative group">
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-gold-500/20 border-2 border-gold-500/50' : 'bg-gold-400/20 border-2 border-gold-400/50'} cursor-pointer hover:border-gold-400 transition`}>
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name || user.email}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <span className={`text-lg font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                          {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-80 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                      <div className={`${theme === 'dark' ? 'bg-black/95 border-2 border-gold-500/30' : 'bg-white border-2 border-gold-400/30'} rounded-xl overflow-hidden backdrop-blur-md`}>
                        {/* User Profile Section */}
                        <div className={`p-4 border-b ${theme === 'dark' ? 'border-gold-500/30' : 'border-gold-400/30'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`relative w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-gold-500/30' : 'bg-gold-400/30'}`}>
                              {user.avatar ? (
                                <img
                                  src={user.avatar}
                                  alt={user.name || user.email}
                                  className="w-full h-full object-cover rounded-full"
                                />
                              ) : (
                                <span className={`text-xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                                  {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold text-base break-words ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title={user.email || user.name}>
                                {user.email || user.name}
                              </p>
                              <Link
                                to="/profile"
                                className={`text-xs ${theme === 'dark' ? 'text-gray-400 hover:text-gold-400' : 'text-gray-600 hover:text-gold-500'} transition`}
                              >
                                Xem hồ sơ
                              </Link>
                            </div>
                          </div>
                        </div>

                        {/* Check-in Button */}
                        <div className="p-4">
                          <Link
                            to="/checkin"
                            className={`w-full py-3 px-4 bg-gold-gradient text-black font-bold text-sm rounded-lg hover:shadow-lg hover:shadow-gold-500/50 transition transform hover:scale-105 flex items-center justify-center gap-2`}
                          >
                            <i className="pi pi-calendar text-lg"></i>
                            <span>ĐIỂM DANH HÔM NAY</span>
                          </Link>
                        </div>

                        {/* Statistics Cards */}
                        <div className="px-4 pb-4 grid grid-cols-3 gap-2">
                          {/* Streak */}
                          <div className={`rounded-lg p-3 text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                            <i className={`pi pi-bolt text-xl mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}></i>
                            <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.streak || 0}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Chuỗi</p>
                          </div>

                          {/* Points */}
                          <div className={`rounded-lg p-3 text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                            <i className={`pi pi-trophy text-xl mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}></i>
                            <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{(user as any)?.total_points || user.points || 0}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Điểm</p>
                          </div>

                          {/* Days Remaining */}
                          <div className={`rounded-lg p-3 text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                            <i className={`pi pi-calendar text-xl mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}></i>
                            <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.daysRemaining || 0}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Ngày còn</p>
                          </div>
                        </div>

                        {/* Logout Button */}
                        <div className={`p-4 border-t ${theme === 'dark' ? 'border-gold-500/30' : 'border-gold-400/30'}`}>
                          <button
                            onClick={() => {
                              logout()
                              navigate('/')
                            }}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition ${theme === 'dark' ? 'text-red-400 hover:bg-red-900/20 hover:text-red-300' : 'text-red-500 hover:bg-red-100 hover:text-red-600'}`}
                          >
                            <i className="pi pi-sign-out text-lg"></i>
                            <span className="font-semibold">Đăng Xuất</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative w-10 h-10 flex items-center justify-center text-gold-400 hover:text-gold-300 transition-colors duration-300 rounded-lg hover:bg-gold-900/20 active:scale-95"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <div>
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className={`lg:hidden fixed inset-0 backdrop-blur-sm z-40 ${theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/50'
                }`}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu */}
            <nav
              ref={mobileMenuRef}
              className={`lg:hidden fixed top-0 left-0 right-0 h-screen overflow-y-auto border-b-2 border-gold-500 shadow-xl z-50 ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'
                }`}
            >
              <div className="container mx-auto px-4 py-4">
                {/* Header with Logo and Close Button */}
                <div className="flex items-center justify-between h-14 mb-4 pb-3 border-b border-gold-500/30">
                  {/* Logo */}
                  <Link
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 hover:opacity-80 transition-all duration-300 flex-shrink-0"
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src="/images/logo.png"
                        alt="SAIGON FC Logo"
                        width={40}
                        height={40}
                        className="object-contain"
                      />
                    </div>
                    <div className="flex flex-col leading-tight min-w-0 items-start">
                      <span className={`font-bold text-sm whitespace-nowrap ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                        SAIGON FC
                      </span>
                      <span className={`font-semibold text-[10px] whitespace-nowrap ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Training League
                      </span>
                    </div>
                  </Link>

                  {/* Close Button */}
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 active:scale-95 ${theme === 'dark'
                      ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                      }`}
                    aria-label="Close menu"
                  >
                    <span className="text-xl font-bold">×</span>
                  </button>
                </div>

                <div className="flex flex-col gap-2 pb-4">
                  {authenticatedNavigationItems.map((item) => {
                    const isActive = pathname === item.href
                    const isReportsPage = item.href === '/reports-appeals'
                    return (
                      <div key={item.href}>
                        <Link
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center gap-3 py-3.5 px-4 rounded-lg
                              border-l-4 transition-all duration-300 relative
                              ${theme === 'dark'
                              ? isActive
                                ? 'border-gold-400 text-gold-300 bg-gold-900/30 font-bold shadow-lg shadow-gold-500/20'
                                : 'border-transparent text-white hover:border-gold-500 hover:text-gold-300 hover:bg-gold-900/20 active:bg-gold-900/30'
                              : isActive
                                ? 'border-gold-500 text-gray-900 bg-gold-400/30 font-bold shadow-lg shadow-gold-500/20'
                                : 'border-transparent text-gray-800 hover:border-gold-500 hover:text-gray-900 hover:bg-gold-400/20 active:bg-gold-400/30'
                            }
                          `}>
                          {item.icon && (
                            <i
                              className={`${item.icon} text-xl transition-transform duration-300`}
                            />
                          )}

                          <span className="font-semibold text-base">{item.name}</span>

                          {isReportsPage && reportsCount > 0 && (
                            <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-bold ${
                              theme === 'dark' ? 'bg-red-500 text-white' : 'bg-red-500 text-white'
                            }`}>
                              {reportsCount}
                            </span>
                          )}

                          {isActive && !isReportsPage && (
                            <span className="ml-auto w-2 h-2 bg-gold-400 rounded-full" />
                          )}
                        </Link>
                      </div>

                    )
                  })}

                  {/* Theme Toggle Mobile */}
                  <div className="flex items-center justify-center pt-3 mt-2 border-t border-gold-500/30">
                    <button
                      onClick={toggleTheme}
                      className={`w-full py-3.5 px-4 flex items-center justify-center gap-2.5 font-semibold text-base transition-all duration-300 active:scale-95 rounded-lg border-2 border-gold-500/40 ${theme === 'dark'
                        ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20 active:bg-gold-900/30'
                        : 'text-gray-800 hover:text-gray-900 hover:bg-gold-400/20 active:bg-gold-400/30'
                        }`}
                      aria-label="Toggle theme"
                    >
                      {theme === 'dark' ? (
                        <>
                          <i className="pi pi-sun text-xl"></i>
                          <span>Chế độ sáng</span>
                        </>
                      ) : (
                        <>
                          <i className="pi pi-moon text-xl"></i>
                          <span>Chế độ tối</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* User Profile Mobile */}
                  {user && (
                    <div className="flex flex-col gap-3 pt-3 mt-2 border-t border-gold-500/30">
                      {/* User Profile Section */}
                      <div className={`px-4 py-3.5 rounded-lg border-2 border-gold-500/40 ${theme === 'dark' ? 'bg-gold-900/20' : 'bg-gold-400/20'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`relative w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border-2 ${theme === 'dark' ? 'bg-gold-500/30 border-gold-500/50' : 'bg-gold-400/30 border-gold-400/50'}`}>
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt={user.name || user.email}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <span className={`text-xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 flex items-center justify-between min-w-0">
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold text-sm break-words mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title={user.email || user.name}>
                                {user.email || user.name}
                              </p>
                              <Link
                                to="/profile"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`text-xs font-semibold ${theme === 'dark' ? 'text-gold-400 hover:text-gold-300' : 'text-gold-600 hover:text-gold-700'} transition`}
                              >
                                Xem hồ sơ →
                              </Link>
                            </div>
                            <button
                              onClick={() => {
                                logout()
                                setIsMobileMenuOpen(false)
                                navigate('/')
                              }}
                              className={`p-2.5 rounded-lg transition ${theme === 'dark'
                                ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                                : 'text-red-500 hover:text-red-600 hover:bg-red-100'
                                }`}
                              aria-label="Logout"
                            >
                              <i className="pi pi-sign-out text-xl"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Check-in Button */}
                      <Link
                        to="/checkin"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`w-full py-4 px-4 bg-gold-gradient text-black font-bold text-base rounded-lg shadow-lg shadow-gold-500/30 transition transform active:scale-95 flex items-center justify-center gap-2.5`}
                      >
                        <i className="pi pi-calendar text-xl"></i>
                        <span>ĐIỂM DANH HÔM NAY</span>
                      </Link>

                      {/* Statistics Cards */}
                      <div className="grid grid-cols-3 gap-2.5">
                        {/* Streak */}
                        <div className={`rounded-lg p-3.5 text-center border-2 ${theme === 'dark' ? 'bg-black/60 border-gold-500/30' : 'bg-gray-100 border-gold-400/30'}`}>
                          <i className={`pi pi-bolt text-xl mb-1.5 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}></i>
                          <p className={`text-xl font-bold mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.streak || 0}</p>
                          <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Chuỗi</p>
                        </div>

                        {/* Points */}
                        <div className={`rounded-lg p-3.5 text-center border-2 ${theme === 'dark' ? 'bg-black/60 border-gold-500/30' : 'bg-gray-100 border-gold-400/30'}`}>
                          <i className={`pi pi-trophy text-xl mb-1.5 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}></i>
                          <p className={`text-xl font-bold mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{(user as any)?.total_points || user.points || 0}</p>
                          <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Điểm</p>
                        </div>

                        {/* Days Remaining */}
                        <div className={`rounded-lg p-3.5 text-center border-2 ${theme === 'dark' ? 'bg-black/60 border-gold-500/30' : 'bg-gray-100 border-gold-400/30'}`}>
                          <i className={`pi pi-calendar text-xl mb-1.5 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}></i>
                          <p className={`text-xl font-bold mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.daysRemaining || 0}</p>
                          <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Ngày còn</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </nav>
          </>
        )}
      </div>
    </header>
  )
}


